/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import { timestampWithMs } from '@sentry/utils';

/**
 * Sentry integration for browser which saves the error events to LocalStorage
 * when the device is offline and resends them when it goes back online.
 *
 * This integration should be inserted as last in the integrations array,
 * because it won't back up changes made to events by any latter integrations.
 *
 * You should call method `sendEvents` to try send the stored events after
 * the Sentry is inicialized.
 * Integration can't do that on it's own.
 *
 * You should add the integration only to the current Sentry hub,
 * due to the limitations of integrations API.
 * The restored events will be reported using the hub which was current during
 * the integration setup.
 *
 * ```javascript
 * const offlineIntegration = new SentryOfflineIntegration();
 * Sentry.init({
 *   dsn: 'https://...',
 *   integrations: (integrations) => [...integrations, offlineIntegration]
 * })
 * offlineIntegration.sendEvents();
 * ```
 */
class SentryOfflineIntegration {
  /**
   * @param {Object} [opts={}]
   * @param {EventTarget} [opts.eventTarget] Source of online / offline events
   * @param {string} [opts.key] Storage key, where the Sentry events are stored.
   * @param {number} [opts.maxEvents] Maximum of Sentry events stored.
   * High values can fill up the storage and disable the back-up.
   * @param {NavigatorOnLine} [opts.navigator] Provider of current onLine value
   * @param {Storage} [opts.storage] Web Storage for backing up Sentry events
   */
  constructor({
    eventTarget = window,
    key = 'SentryOfflineIntegration',
    maxEvents = 5,
    navigator = window.navigator,
    storage = window.localStorage
  } = {}) {
    this.eventTarget = eventTarget;
    this.hub = null;
    this.key = key;
    this.maxEvents = maxEvents;
    this.name = SentryOfflineIntegration.id;
    this.navigator = navigator;
    this.storage = storage;
  }

  /**
   * Clear all Sentry events from the storage.
   * @private
   * @returns {undefined}
   */
  clearEvents() {
    this.storage.removeItem(this.key);
  }

  /**
   * Process the global Sentry event and back it up, if necessary.
   * @param {SentryEvent} event
   * @private
   * @returns {SentryEvent|null} Processed event or null to ignore it
   */
  processEvent(event) {
    // If the device is online, do nothing and send the event immediately.
    if (this.navigator.onLine) {
      return event;
    }
    // If the device is offline, add the event to the backup.
    this.storeEvent(event);
    return null;
  }

  /**
   * Restore all Sentry events from the storage.
   * @private
   * @returns {Array.<SentryEvent>}
   */
  restoreEvents() {
    const json = this.storage.getItem(this.key);
    if (json === null) {
      return [];
    }
    return JSON.parse(json);
  }

  /**
   * Send all stored Sentry events to server and clear them from the storage.
   * Events are sent at once, Sentry should handle queing etc.
   * You can use this method to send the stored events after client setup.
   * @returns {undefined}
   */
  sendEvents() {
    const { hub } = this;
    const client = hub.getClient();
    if (!client) {
      // Do not send the events if hub is not initialized.
      return;
    }
    const dsn = client.getDsn();
    if (!dsn) {
      // Do not send the events if client doesn't have DSN.
      return;
    }
    if (!this.navigator.onLine) {
      // Do not send the events when offline.
      return;
    }
    const events = this.restoreEvents();
    events.forEach((event) => hub.captureEvent(event));
    this.clearEvents();
  }

  /**
   * Setup the Sentry integration.
   * @param {Function} addGlobalEventProcessor
   * @param {Function} getCurrentHub
   * @returns {undefined}
   */
  setupOnce(addGlobalEventProcessor, getCurrentHub) {
    // Try to use the hub, where the integration was created.
    this.hub = getCurrentHub();
    // Start processing the events.
    addGlobalEventProcessor((...args) => this.processEvent(...args));
    // Start watching for online events.
    this.eventTarget.addEventListener('online', () => this.sendEvents());
  }

  /**
   * Store Sentry event in the storage.
   * This can remove the oldest backed up event, if maximum is reached.
   * @param {SentryEvent} event
   * @private
   * @returns {undefined}
   */
  storeEvent(event) {
    // Ensure the event timestamp is set to time, when the event is processed,
    // otherwise it will be set to time when the event is sent.
    event.timestamp = timestampWithMs();
    // Add the event to persistent backup.
    const events = this.restoreEvents()
      .concat(event)
      .slice(-1 * this.maxEvents);
    const json = JSON.stringify(events);
    this.storage.setItem(this.key, json);
  }
}

SentryOfflineIntegration.id = 'Offline';

export default SentryOfflineIntegration;
